import { canAccessSchoolDashboardRoute } from "~/utils/organizationPermissions";
import { setCookie } from "~/repository/cookieService";
import { getUserAccessCookie, hasAccessToken } from "~/repository/authSession";
import type { UserAccessCookie } from "~/types/auth";
import { useAuthStore } from "~/stores/auth";
import { useIndividualReceivingAccess } from "~/composables/useIndividualReceivingAccess";
import { isDocumentWorkspaceRoute } from "~/utils/documentAccess";
import { studentDashboardRedirect } from "~/utils/studentAccess";
import { useStudentGamificationAccess } from "~/composables/useStudentGamificationAccess";
import {
  canAccessSchoolTeacher,
  canAccessSchoolTeacherRoute,
  schoolEntryPath,
  schoolTeacherEquivalent,
} from "~/utils/teacherExperience";
import {
  canCorrectOfficialMaterials,
  isContentQualityReviewerRoute,
  shouldDeferContentQualityAuthorization,
} from "~/utils/adminCapabilities";

const checkIfUrlIsInArray = (url: string, array: string[]) => {
  for (const item of array) {
    if (url === item || url === `${item}/`) {
      return true;
    }

    if (item.endsWith("*") && url.startsWith(item.slice(0, -1))) {
      return true;
    }
  }

  return false;
};

export default defineNuxtRouteMiddleware(async (to, _) => {
  const permissions: UserAccessCookie = getUserAccessCookie() || {
    isTeacher: false,
    isStudent: false,
    isStaff: false,
    isSuperuser: false,
    adminCapabilities: [],
  };

  const NO_LOGIN_REQUIRED = [
    "/",
    "/login",
    "/school-login",
    "/school-access",
    "/store-maintenance/login",
    "/store-maintenance/orders",
    "/logout",
    "/password-reset",
    "/password-reset/*",
    "/signup",
    "/signup/*",
    "/code",
    "/code/*",
    "/v",
    "/v/*",
    "/video-call",
    "/video-call/*",
    "/assessments/public/*",
    "/forms/*",
    "/book/*",
    "/avaliacoes/publicas/*",
  ];

  const TEACHER_REQUIRED = ["/dashboard/", "/dashboard/*"];
  const SCHOOL_DASHBOARD_REQUIRED = [
    "/school-dashboard",
    "/school-dashboard/",
    "/school-dashboard/*",
  ];

  const STUDENT_REQUIRED = ["/student-dashboard/", "/student-dashboard/*"];

  const STAFF_REQUIRED = ["/admin-dashboard/", "/admin-dashboard/*"];

  const isNoLoginRequired = checkIfUrlIsInArray(to.path, NO_LOGIN_REQUIRED);

  if (isNoLoginRequired) {
    if (
      !["/login", "/school-login"].includes(to.path) &&
      !to.path.startsWith("/password-reset")
    ) {
      setCookie("originalUrl", "");
    }
    return;
  }

  const isTeacherRequired = checkIfUrlIsInArray(to.path, TEACHER_REQUIRED);
  const isSchoolTeacherRequired = /^\/school-teacher(?:\/|$)/.test(to.path);
  // Document routes use their authoritative profile capability, including managers without a teacher profile.
  const deferDocumentAuthorization =
    isDocumentWorkspaceRoute(to.path) && hasAccessToken();
  const isSchoolDashboardRequired = checkIfUrlIsInArray(
    to.path,
    SCHOOL_DASHBOARD_REQUIRED,
  );
  const isStudentRequired = checkIfUrlIsInArray(to.path, STUDENT_REQUIRED);
  const isStaffRequired = checkIfUrlIsInArray(to.path, STAFF_REQUIRED);
  const hasReviewerAccess = canCorrectOfficialMaterials(
    permissions.adminCapabilities,
  );
  const isAllowedReviewerRoute = isContentQualityReviewerRoute(to.path);

  // The `ua` cookie is only a navigation hint and may predate admin
  // capabilities. Authenticated reviewer routes delegate the authoritative
  // check to `content-quality-reviewer`, which always refreshes `/profile/me`.
  if (
    isStaffRequired &&
    shouldDeferContentQualityAuthorization(to.path, hasAccessToken())
  ) {
    return;
  }

  if (
    isStaffRequired &&
    hasReviewerAccess &&
    !permissions.isStaff &&
    !permissions.isSuperuser &&
    !isAllowedReviewerRoute
  ) {
    return navigateTo("/admin-dashboard/content-quality");
  }

  if (
    (isTeacherRequired &&
      !permissions.isTeacher &&
      !deferDocumentAuthorization) ||
    (isSchoolDashboardRequired && !hasAccessToken()) ||
    (isSchoolTeacherRequired && !hasAccessToken()) ||
    (isStudentRequired && !permissions.isStudent) ||
    (isStaffRequired &&
      !permissions.isStaff &&
      !permissions.isSuperuser &&
      !(hasReviewerAccess && isAllowedReviewerRoute))
  ) {
    setCookie("originalUrl", to.fullPath);
    alert(
      "Faça login para acessar esta página. Caso já tenha feito, verifique se o seu usuário tem permissão para acessá-la.",
    );
    return navigateTo("/login");
  }

  if (
    (isTeacherRequired && permissions.isTeacher) ||
    deferDocumentAuthorization ||
    isSchoolDashboardRequired ||
    isSchoolTeacherRequired ||
    isStudentRequired
  ) {
    const authStore = useAuthStore();
    if (
      !authStore.profileLoaded ||
      isStudentRequired ||
      isSchoolDashboardRequired ||
      isSchoolTeacherRequired ||
      (isTeacherRequired && Boolean(authStore.user.activeOrganization))
    ) {
      if (isSchoolTeacherRequired) {
        // Hide the previous school's data while authoritative access is being
        // refreshed. A failed refresh must not authorize a cached profile.
        authStore.profileLoaded = false;
        try {
          await authStore.fetchUserData();
        } catch {
          throw createError({
            statusCode: 503,
            statusMessage: "Não foi possível verificar seu acesso à escola.",
          });
        }
      } else {
        await authStore.fetchUserData();
      }
    }
    if (isSchoolTeacherRequired) {
      if (!authStore.user.activeOrganization) {
        return navigateTo(
          authStore.user.individualContextAllowed &&
            authStore.user.availableOrganizations.length === 0 &&
            authStore.user.permissions.isTeacher
            ? "/dashboard/home"
            : `/school-select?redirect=${encodeURIComponent(to.fullPath)}`,
          { replace: true },
        );
      }
      if (!canAccessSchoolTeacher(authStore.user))
        return navigateTo(schoolEntryPath(authStore.user), { replace: true });
      if (!canAccessSchoolTeacherRoute(authStore.user, to.path))
        return navigateTo("/school-teacher/home", { replace: true });
      return;
    }
    if (
      isTeacherRequired &&
      authStore.user.activeOrganization &&
      !deferDocumentAuthorization
    ) {
      const destination = canAccessSchoolTeacher(authStore.user)
        ? schoolTeacherEquivalent(to.fullPath) ||
          schoolEntryPath(authStore.user)
        : schoolEntryPath(authStore.user);
      return navigateTo(destination, { replace: true });
    }
    if (isStudentRequired) {
      const redirect = studentDashboardRedirect(to.path, {
        isBlocked: authStore.user.studentAccess.isBlocked,
        canAccessLearning: authStore.user.studentAccess.canAccessLearning,
        allowedSections: authStore.user.studentAccess.allowedSections,
        hasSchool: Boolean(authStore.user.studentAccess.schoolContact),
      });
      if (redirect) return navigateTo(redirect);
      if (
        /^\/student-dashboard\/rewards(?:\/|$)/.test(to.path) &&
        !useStudentGamificationAccess().value
      ) {
        return navigateTo("/student-dashboard/home");
      }
    }
    if (isSchoolDashboardRequired && !authStore.user.activeOrganization) {
      return navigateTo(
        `/school-select?redirect=${encodeURIComponent(to.fullPath)}`,
      );
    }
    if (
      isSchoolDashboardRequired &&
      !canAccessSchoolDashboardRoute(
        authStore.user.activeOrganization,
        to.path,
        authStore.hasSchoolDocumentsAccess,
      )
    ) {
      if (authStore.hasSchoolManagementAccess)
        return navigateTo("/school-dashboard");
      return navigateTo(
        canAccessSchoolTeacher(authStore.user)
          ? "/school-teacher/home"
          : "/school-select",
      );
    }
    // The individual UI requires its rollout grant, including migrated history.
    // Historical API reads and mutation capabilities remain backend-owned.
    const isIndividualReceivingHistory =
      isTeacherRequired && /^\/dashboard\/receiving\/?$/.test(to.path);
    if (isIndividualReceivingHistory && !useIndividualReceivingAccess().value)
      return navigateTo(
        authStore.requiresSchoolContext ? "/school-select" : "/dashboard/home",
      );
    if (authStore.requiresSchoolContext && !isIndividualReceivingHistory) {
      return navigateTo(
        `/school-select?redirect=${encodeURIComponent(to.fullPath)}`,
      );
    }
  }
});
